import React, { useState, useEffect, useMemo } from 'react';
import { Dropdown } from '../../components/Dropdown'; 
import { Button } from '../../components/Button';     
import indiaData from '../../data/stateData.json';    
import { apiGet } from '../../services/apiServices';
import { endPoints } from '../../configs/apiUrls';

const initialFilterState = {
  status: "",
  state: "",
  city: "",
  area: "",
  valuer: "",
  fromDate: "",
  toDate: "",
};

const statusOptions = [
  { label: "All Statuses", value: "" },
  { label: "Requested", value: "Requested" },
  { label: "Assigned", value: "Assigned" },
  { label: "Request Accepted", value: "Request Accepted" },
  { label: "Valuer Rejected", value: "Valuer Rejected" },
  { label: "Completed", value: "Completed" },
];

const RequestSearchFilterForm = ({ tableRef, filterData, setFilterData }: any) => {
  const [filters, setFilters] = useState(initialFilterState);
  
  // States for dynamic dropdowns
  const [areaOptions, setAreaOptions] = useState<any[]>([]);

  // Sync local filter state with table filter state when drawer opens
  useEffect(() => {
    setFilters({
      status: filterData.status || "",
      state: filterData.state || "",
      city: filterData.city || "",
      area: filterData.area || "",
      valuer: filterData.valuer || "",
      fromDate: filterData.fromDate || "",
      toDate: filterData.toDate || "",
    });

    if (filterData.city) fetchAreas(filterData.city);
  }, [filterData]);

  // --- API Fetchers ---
  const fetchAreas = async (cityName: string) => {
    try {
      const res: any = await apiGet(`${endPoints.GET_AREA_LIST_BY_CITY}/${cityName}`);
      if (res?.success) {
        const formatted = res.data.map((item: any) => ({
          label: item.label || item.area || item.areaName,
          value: item.value || item._id
        }));
        setAreaOptions(formatted);
      }
    } catch (error) {
      console.error("Failed to fetch areas", error);
    }
  };

  // --- Change Handlers ---
  const onFilterChange = (field: string, value: string | null) => {
    if (field === "state") {
      setFilters((prev) => ({ ...prev, state: value || "", city: "", area: "" }));
      setAreaOptions([]);
      return;
    }
    
    if (field === "city") {
      setFilters((prev) => ({ ...prev, city: value || "", area: "" }));
      if (value) fetchAreas(value);
      else setAreaOptions([]);
      return;
    }

    setFilters((prev) => ({ ...prev, [field]: value || "" }));
  };

  // --- Static Options ---
  const stateOptions = useMemo(() => {
    return indiaData.map((s: any) => ({ label: s.state, value: s.state }));
  }, []);

  const cityOptions = useMemo(() => {
    if (!filters.state) return [];
    const stateObj = indiaData.find((s: any) => s.state === filters.state);
    if (!stateObj) return [];
    return stateObj.districts.map((d: string) => ({ label: d, value: d }));
  }, [filters.state]);

  // --- Action Buttons ---
  const handleFilter = () => {    
    setFilterData((prev: any) => ({
      ...prev,
      ...filters,
      page: 1, // Reset to page 1 on new filter
    }));
    if (tableRef?.current) {
      tableRef.current.closeFilterDrawer();
    }
  };

  const handleClear = () => {
    setFilterData((prev: any) => ({
      ...prev,
      ...initialFilterState,
      page: 1,
    }));
    setFilters(initialFilterState);
    setAreaOptions([]);
    
    if (tableRef?.current) {
      tableRef.current.closeFilterDrawer();
    }
  };

  return (
    <div className="h-full flex flex-col p-4">
      <div className="flex-grow overflow-auto pb-6">
        <div className="flex flex-col gap-4">
          
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <Dropdown
              options={statusOptions}
              value={filters.status}
              onChange={(val: string | null) => onFilterChange("status", val)}
              placeholder="Filter by Status"
              className="w-full"
            />
          </div>

          {/* Location Filters */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
            <Dropdown
              options={stateOptions}
              value={filters.state}
              onChange={(val: string | null) => onFilterChange("state", val)}
              placeholder="Select state"
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <Dropdown
              options={cityOptions}
              value={filters.city}
              onChange={(val: string | null) => onFilterChange("city", val)}
              placeholder={!filters.state ? "Select State First" : "Select city"}
              disabled={!filters.state}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
            <Dropdown
              options={areaOptions}
              value={filters.area}
              onChange={(val: string | null) => onFilterChange("area", val)}
              placeholder={!filters.city ? "Select City First" : "Select area"}
              disabled={!filters.city}
              className="w-full"
            />
          </div>

          {/* Assigned Valuer Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Valuer Name</label>
            <input
              type="text"
              placeholder="Search by valuer name..."
              value={filters.valuer}
              onChange={(e) => onFilterChange("valuer", e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 shadow-sm text-sm text-gray-700 focus:ring-2 focus:ring-blue-600 focus:outline-none"
            />
          </div>

          <hr className="my-2 border-gray-200" />

          {/* Date Range Filters */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => onFilterChange("fromDate", e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 shadow-sm text-sm text-gray-700 focus:ring-2 focus:ring-blue-600 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={filters.toDate}
              min={filters.fromDate}
              onChange={(e) => onFilterChange("toDate", e.target.value)}
              className="w-full rounded border border-gray-300 px-3 py-2 shadow-sm text-sm text-gray-700 focus:ring-2 focus:ring-blue-600 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
              disabled={!filters.fromDate}
            />
          </div>

        </div>
      </div>
      
      {/* Fixed footer buttons */}
      <div className="border-t pt-4 flex justify-end gap-3 bg-white flex-shrink-0">
        <Button onClick={handleClear} variant="outline">
          Clear
        </Button>
        <Button onClick={handleFilter}>Apply Filters</Button>
      </div>
    </div>
  );
};

export default RequestSearchFilterForm;